import { Delete, ExpandMore, History } from "@mui/icons-material";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { GridRowId, GridRowModel } from "@mui/x-data-grid-pro";
import { GridBulkActionsItem } from "components/DataGrid/components/GridBulkActionsItem";
import { RevivnDatabaseDataGrid } from "components/DataGrid/RevivnDatabaseDataGrid";
import InventoryPartEventsDrawer from "components/InventoryParts/EventsDrawer";
import { WarehouseLocationLink } from "components/Locations/WarehouseLocationLink";
import { useGlobalAlert } from "hooks/useAlert";
import { useConfirmation } from "hooks/useConfirmation";
import _ from "lodash";
import { PageBreadcrumbs, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { FullPagePaperLayout } from "pages/Layouts/FullPagePaperLayout";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "services/api.service";
import { InventoryPart } from "types/inventoryPart";
import { pluralize } from "utils/helpers";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ConfirmOptions = (handler: any, message?: string, title?: string, context?: string) => void;

function BulkDeleteAction({ confirm }: { confirm: ConfirmOptions }) {
  const handleBulkDelete = (selectedRows: Map<GridRowId, GridRowModel>) => {
    const inventoryParts = Array.from(selectedRows.values()) as InventoryPart[];
    const numParts = pluralize(inventoryParts.length, "part", "parts");

    confirm({
      handler: () =>
        api
          .delete("inventory_parts/bulk", { data: { inventoryParts: inventoryParts } })
          .then(() => useGlobalAlert.alertSuccess(`${numParts} deleted.`))
          .catch((error) => useGlobalAlert.alertError(`Failed to delete parts: Status ${error.statusText}`)),
      message: `Are you sure you want to delete ${numParts}?`,
    });
  };

  return <GridBulkActionsItem icon={<Delete />} label="Delete" onClick={handleBulkDelete} />;
}

function RowActionMenu({
  row,
  historyAction,
}: {
  row: InventoryPart;
  historyAction: (inventoryPartId: number) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  return (
    <>
      <IconButton size="small" onClick={handleOpenMenu}>
        <ExpandMore />
      </IconButton>
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
        <MenuItem
          onClick={() => {
            historyAction(row.id);
            handleCloseMenu();
          }}
        >
          <ListItemIcon>
            <History fontSize="small" />
          </ListItemIcon>
          <ListItemText>View History</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default function InventoryPartsIndexPage() {
  const { confirm } = useConfirmation();
  const getBulkActions = useCallback(() => [<BulkDeleteAction confirm={confirm} />], [confirm]);
  const [showEvents, setShowEvents] = useState(false);
  const [inventoryPartId, setInventoryPartId] = useState<number | undefined>(undefined);

  const handleHistoryAction = useCallback(
    (newInventoryPartId: number) => {
      setInventoryPartId(newInventoryPartId);
      setShowEvents(true);
    },
    [setInventoryPartId, setShowEvents],
  );

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Warehouse" }, { label: "Inventory Parts" }]} />
      <PageTitle>Inventory Parts</PageTitle>

      <RevivnDatabaseDataGrid
        url="inventory_parts"
        getBulkActions={getBulkActions}
        checkboxSelection
        disableSelectionOnClick
        density="compact"
        syncStateWithSearchParams
        columns={[
          { field: "id", headerName: "ID", type: "string" },
          { field: "label", headerName: "Label", minWidth: 150 },
          {
            field: "type",
            headerName: "Type",
            valueFormatter(params) {
              switch (params.value) {
                case "InventoryParts::Lcd":
                  return "LCD";
                case "InventoryParts::TopCase":
                  return "Top Case";
                case "InventoryParts::Battery":
                  return "Battery";
                case "InventoryParts::Board":
                  return "Board";
                default:
                  return params.value;
              }
            },
          },
          {
            field: "warehouseLocation",
            headerName: "Location",
            minWidth: 150,
            renderCell: ({ row }) => <WarehouseLocationLink code={row.warehouseLocation} locationId={row.locationId} />,
          },
          {
            field: "state",
            headerName: "State",
            valueFormatter: (params) => _.startCase(params.value),
          },
          {
            field: "parentInventoryId",
            headerName: "Parent Inventory",
            renderCell(params) {
              return (
                <Link
                  to={`/technician/${params.value}/inventory-parts/${_.snakeCase(params.row.type.split("::").pop())}`}
                >
                  {params.value}
                </Link>
              );
            },
            width: 150,
          },
          {
            field: "currentInventoryId",
            headerName: "Current Inventory",
            renderCell(params) {
              return (
                <Link
                  to={`/technician/${params.value}/inventory-parts/${_.snakeCase(params.row.type.split("::").pop())}`}
                >
                  {params.value}
                </Link>
              );
            },
            width: 150,
          },
          { field: "sku", headerName: "SKU", width: 150 },
          { field: "apn", headerName: "APN" },
          { field: "cosmeticGrade", headerName: "Grade" },
          { field: "comments", headerName: "Comments", width: 400 },
          { field: "model", headerName: "Model" },
          {
            field: "actions",
            headerName: "",
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            disableExport: true,
            disableReorder: true,
            align: "center",
            renderCell(params) {
              return <RowActionMenu row={params.row} historyAction={handleHistoryAction} />;
            },
          },
        ]}
      />
      <InventoryPartEventsDrawer open={showEvents} setOpen={setShowEvents} inventoryPartId={inventoryPartId} />
    </FullPagePaperLayout>
  );
}
